// Instant Tenant Score - Green, Yellow, or Red. One glance decision!
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Divider,
  InputAdornment
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  Cancel,
  AttachMoney,
  CreditScore,
  Work
} from "@mui/icons-material";
import { useAuth } from "contexts/AuthContext";

// Client-side tenant scoring logic
function computeTenantScore(data) {
  const creditScore = parseInt(data.credit_score) || 0;
  const monthlyIncome = parseFloat(data.monthly_income) || 0;
  const requestedRent = parseFloat(data.requested_rent) || 0;
  const employmentStatus = data.employment_status || "employed";
  const evictionHistory = data.eviction_history || 0;
  const criminalRecord = data.criminal_record || false;
  const landlordReference = data.landlord_reference || "positive";

  const reasons = [];
  const details = {
    can_afford: requestedRent > 0 ? `${((monthlyIncome / requestedRent) * 100).toFixed(0)}%` : "N/A",
    employed: employmentStatus === "employed",
    evictions: evictionHistory,
  };

  let points = 0;

  // Credit score evaluation (0-40 points)
  if (creditScore >= 750) {
    points += 40;
    reasons.push("Excellent credit score");
  } else if (creditScore >= 700) {
    points += 30;
    reasons.push("Good credit score");
  } else if (creditScore >= 650) {
    points += 20;
    reasons.push("Fair credit score");
  } else if (creditScore >= 600) {
    points += 10;
    reasons.push("Below average credit score");
  } else {
    reasons.push("Poor credit score");
  }

  // Income-to-rent ratio (0-30 points)
  if (requestedRent > 0) {
    const ratio = monthlyIncome / requestedRent;
    if (ratio >= 3) {
      points += 30;
      reasons.push("Income is 3x+ rent");
    } else if (ratio >= 2.5) {
      points += 20;
      reasons.push("Income is 2.5x rent");
    } else if (ratio >= 2) {
      points += 10;
      reasons.push("Income is 2x rent - tight budget");
    } else {
      reasons.push("Income below 2x rent - may struggle to pay");
    }
  }

  // Employment (0-10 points)
  if (employmentStatus === "employed") {
    points += 10;
  } else {
    reasons.push("Not currently employed");
  }

  // Eviction history (-20 points each)
  if (evictionHistory > 0) {
    points -= 20 * evictionHistory;
    reasons.push(`${evictionHistory} prior eviction(s)`);
  }

  // Criminal record (-15 points)
  if (criminalRecord) {
    points -= 15;
    reasons.push("Criminal record on file");
  }

  // Landlord reference (0-20 points)
  if (landlordReference === "positive") {
    points += 20;
    reasons.push("Positive landlord reference");
  } else if (landlordReference === "neutral") {
    points += 10;
    reasons.push("Neutral landlord reference");
  } else {
    reasons.push("Negative landlord reference");
  }

  // Determine score color
  let score;
  let message;
  if (points >= 70) {
    score = "GREEN";
    message = "Great Tenant - Approve!";
  } else if (points >= 40) {
    score = "YELLOW";
    message = "Proceed with Caution";
  } else {
    score = "RED";
    message = "High Risk - Consider Declining";
  }

  return { score, message, reasons, details };
}

function InstantTenantScore({ onScoreCalculated, initialData }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState(null);
  const [formData, setFormData] = useState({
    credit_score: initialData?.credit_score || "",
    monthly_income: initialData?.monthly_income || "",
    requested_rent: initialData?.requested_rent || "",
    employment_status: initialData?.employment_status || "employed",
    eviction_history: initialData?.eviction_history || 0,
    criminal_record: initialData?.criminal_record || false,
    landlord_reference: initialData?.landlord_reference || "positive"
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateScore = async () => {
    setLoading(true);
    try {
      const result = computeTenantScore(formData);
      setScore(result);
      if (onScoreCalculated) {
        onScoreCalculated(result);
      }
    } catch (error) {
      console.error("Error calculating score:", error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreIcon = () => {
    if (!score) return null;

    switch (score.score) {
      case "GREEN":
        return <CheckCircle sx={{ fontSize: 80, color: "success.main" }} />;
      case "YELLOW":
        return <Warning sx={{ fontSize: 80, color: "warning.main" }} />;
      case "RED":
        return <Cancel sx={{ fontSize: 80, color: "error.main" }} />;
      default:
        return null;
    }
  };

  const getScoreColor = () => {
    if (!score) return "default";

    switch (score.score) {
      case "GREEN":
        return "success";
      case "YELLOW":
        return "warning";
      case "RED":
        return "error";
      default:
        return "default";
    }
  };

  const reset = () => {
    setScore(null);
    setFormData({
      credit_score: "",
      monthly_income: "",
      requested_rent: "",
      employment_status: "employed",
      eviction_history: 0,
      criminal_record: false,
      landlord_reference: "positive"
    });
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom align="center">
        Instant Tenant Score
      </Typography>

      {!score ? (
        <Box>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter basic info for instant Green/Yellow/Red decision
          </Typography>

          {/* Simple 3-field form */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Credit Score"
              type="number"
              value={formData.credit_score}
              onChange={(e) => handleInputChange("credit_score", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CreditScore />
                  </InputAdornment>
                ),
              }}
              placeholder="e.g., 720"
            />

            <TextField
              label="Monthly Income"
              type="number"
              value={formData.monthly_income}
              onChange={(e) => handleInputChange("monthly_income", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
                ),
              }}
              placeholder="e.g., 5000"
            />

            <TextField
              label="Requested Rent"
              type="number"
              value={formData.requested_rent}
              onChange={(e) => handleInputChange("requested_rent", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoney />
                  </InputAdornment>
                ),
              }}
              placeholder="e.g., 1500"
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={calculateScore}
            disabled={loading}
            sx={{
              mt: 3,
              py: 2,
              fontSize: "1.1rem",
              background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
              boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
            }}
          >
            {loading ? "Calculating..." : "Get Instant Score"}
          </Button>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          {/* Big visual score */}
          {getScoreIcon()}

          <Typography variant="h4" sx={{ mt: 2, mb: 1, fontWeight: "bold" }}>
            {score.message}
          </Typography>

          <Chip
            label={score.score}
            color={getScoreColor()}
            size="large"
            sx={{ mb: 3, fontSize: "1.2rem", py: 3, px: 4 }}
          />

          {/* Reasons */}
          {score.reasons && score.reasons.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Key Factors:
              </Typography>
              {score.reasons.map((reason, index) => (
                <Typography key={index} variant="body2" sx={{ mt: 1 }}>
                  • {reason}
                </Typography>
              ))}
            </>
          )}

          {/* Quick details */}
          {score.details && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", justifyContent: "space-around", mt: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Can Afford
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {score.details.can_afford}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Employed
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {score.details.employed ? "Yes" : "No"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Evictions
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {score.details.evictions}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          <Button
            variant="outlined"
            onClick={reset}
            sx={{ mt: 3 }}
          >
            Check Another Tenant
          </Button>
        </Box>
      )}
    </Card>
  );
}

InstantTenantScore.propTypes = {
  onScoreCalculated: PropTypes.func,
  initialData: PropTypes.shape({
    credit_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    monthly_income: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    requested_rent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    employment_status: PropTypes.string,
    eviction_history: PropTypes.bool,
    criminal_record: PropTypes.bool,
    landlord_reference: PropTypes.string
  })
};

export default InstantTenantScore;
